// Authentication service for Skribo application
// Extended to support different user types and usage limits

// List of valid users with user type and usage information
const VALID_CREDENTIALS = [
  { 
    username: 'admin', 
    password: 'password123-!2k',
    userType: 'regular', // full access
    whisper_minutes_used: 0,
    notes_generated: 0 
  },
  { 
    username: 'lege', 
    password: 'lege123',
    userType: 'demo', // demo access
    whisper_minutes_used: 0,
    notes_generated: 0 
  },
  { 
    username: 'Sander', 
    password: 'Yahboi123-',
    userType: 'regular',
    whisper_minutes_used: 0,
    notes_generated: 0 
  },
  { 
  username: 'Anette', 
  password: 'Passord123',
  userType: 'demo',
  whisper_minutes_used: 0,
  notes_generated: 0 
},    
{ 
  username: 'Mona', 
password: 'MonaKona123',
userType: 'demo',
whisper_minutes_used: 0,
notes_generated: 0 
},
  { 
    username: 'William', 
    password: 'Passord123',
    userType: 'demo',
    whisper_minutes_used: 0,
    notes_generated: 0 
  },
];

// Function to check if credentials are valid
export const validateCredentials = (username, password) => {
  return VALID_CREDENTIALS.some(
    cred => cred.username === username && cred.password === password
  );
};

// Function to get saved user data from local storage
const getSavedUserData = () => {
  const savedUsersJSON = localStorage.getItem('skriboUsers');
  let savedUsers = [];
  
  if (savedUsersJSON) {
    try {
      savedUsers = JSON.parse(savedUsersJSON);
    } catch (e) {
      console.error("Error parsing saved users:", e);
    }
  }
  
  return savedUsers;
};

// Function to save users to local storage
const saveUserData = (users) => {
  localStorage.setItem('skriboUsers', JSON.stringify(users));
};

// Login function
export const login = (username, password) => {
  return new Promise((resolve, reject) => {
    // Simulate API call delay
    setTimeout(() => {
      // First check hardcoded credentials
      const hardcodedUser = VALID_CREDENTIALS.find(
        cred => cred.username === username && cred.password === password
      );
      
      // Then check saved users
      const savedUsers = getSavedUserData();
      const savedUser = savedUsers.find(
        user => user.username === username && user.password === password
      );
      
      const user = hardcodedUser || savedUser;
      
      if (user) {
        // Generate a consistent user ID based on username instead of timestamp
        const userId = `user_${username.toLowerCase().replace(/\s+/g, '_')}`;
        
        // Store basic auth info
        localStorage.setItem('skriboUserId', userId);
        localStorage.setItem('skriboUsername', username);
        
        // Store user type and usage info
        localStorage.setItem('skriboUserType', user.userType || 'demo');
        localStorage.setItem('skriboWhisperMinutesUsed', user.whisper_minutes_used || 0);
        localStorage.setItem('skriboNotesGenerated', user.notes_generated || 0);
        
        resolve({ 
          userId, 
          username, 
          userType: user.userType || 'demo',
          whisper_minutes_used: user.whisper_minutes_used || 0,
          notes_generated: user.notes_generated || 0
        });
      } else {
        reject(new Error('Ugyldig brukernavn eller passord'));
      }
    }, 500); // Simulate network delay
  });
};

// Register function
export const register = (username, email, password, userType = 'demo') => {
  return new Promise((resolve, reject) => {
    // Simulate API call delay
    setTimeout(() => {
      // Check if username already exists in hardcoded users
      const existingHardcodedUser = VALID_CREDENTIALS.find(cred => cred.username === username);
      
      // Check if username exists in saved users
      const savedUsers = getSavedUserData();
      const existingSavedUser = savedUsers.find(user => user.username === username);
      
      if (existingHardcodedUser || existingSavedUser) {
        reject(new Error('Brukernavn eksisterer allerede'));
        return;
      }
      
      // Create new user
      const newUser = {
        username,
        email,
        password,
        userType: userType || 'demo', // Default to demo if not specified
        whisper_minutes_used: 0,
        notes_generated: 0,
        createdAt: new Date().toISOString()
      };
      
      // Save to local storage
      savedUsers.push(newUser);
      saveUserData(savedUsers);
      
      // Generate consistent user ID based on username instead of timestamp
      const userId = `user_${username.toLowerCase().replace(/\s+/g, '_')}`;
      
      // Store auth info
      localStorage.setItem('skriboUserId', userId);
      localStorage.setItem('skriboUsername', username);
      localStorage.setItem('skriboUserType', userType);
      localStorage.setItem('skriboWhisperMinutesUsed', 0);
      localStorage.setItem('skriboNotesGenerated', 0);
      
      resolve({
        userId,
        username,
        userType,
        whisper_minutes_used: 0,
        notes_generated: 0
      });
    }, 500); // Simulate network delay
  });
};

// Logout function
export const logout = () => {
  localStorage.removeItem('skriboUserId');
  localStorage.removeItem('skriboUsername');
  localStorage.removeItem('skriboUserType');
  localStorage.removeItem('skriboWhisperMinutesUsed');
  localStorage.removeItem('skriboNotesGenerated');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('skriboUserId');
};

// Get current user info
export const getCurrentUser = () => {
  const userId = localStorage.getItem('skriboUserId');
  const username = localStorage.getItem('skriboUsername');
  const userType = localStorage.getItem('skriboUserType') || 'demo';
  const whisper_minutes_used = parseFloat(localStorage.getItem('skriboWhisperMinutesUsed') || 0);
  const notes_generated = parseInt(localStorage.getItem('skriboNotesGenerated') || 0);
  
  if (userId && username) {
    return { 
      userId, 
      username, 
      userType,
      whisper_minutes_used,
      notes_generated
    };
  }
  
  return null;
};

// Update user's usage
export const updateUserUsage = (type, amount) => {
  if (type === 'whisper_time') {
    const currentUsage = parseFloat(localStorage.getItem('skriboWhisperMinutesUsed') || 0);
    const newUsage = currentUsage + amount;
    localStorage.setItem('skriboWhisperMinutesUsed', newUsage);
    return newUsage;
  } else if (type === 'note_generation') {
    const currentUsage = parseInt(localStorage.getItem('skriboNotesGenerated') || 0);
    const newUsage = currentUsage + amount;
    localStorage.setItem('skriboNotesGenerated', newUsage);
    return newUsage;
  }
  return 0;
};

// Check if user has reached usage limits
export const checkUserLimits = () => {
  const userType = localStorage.getItem('skriboUserType') || 'demo';
  
  // Regular users have no limits
  if (userType === 'regular') {
    return { hasReachedLimit: false };
  }
  
  const whisper_minutes_used = parseFloat(localStorage.getItem('skriboWhisperMinutesUsed') || 0);
  const notes_generated = parseInt(localStorage.getItem('skriboNotesGenerated') || 0);
  
  // Check demo account limits
  if (whisper_minutes_used >= 10) {
    return { hasReachedLimit: true, limitType: 'whisper_time' };
  }
  
  if (notes_generated >= 3) {
    return { hasReachedLimit: true, limitType: 'note_generation' };
  }
  
  return { hasReachedLimit: false };
};